// src/services/PresenceSocket.js
//
// Live presence updates, with the poll kept as a fallback.
//
// The display polled every three seconds: 20 requests a minute per screen to
// catch a change that happens a few times an hour. The socket carries the same
// news at the moment it happens and stays silent otherwise.
//
// WHAT ARRIVES
//
// The word "presence". Not the data.
//
// The caller refetches when it hears that. A missed message then costs one
// extra fetch and repairs itself on the next one; sending the state instead
// would leave a board permanently wrong with nothing to notice it.
//
// THE FALLBACK IS THE POINT
//
// A wall screen runs unattended for weeks. If the socket dies and nothing
// notices, the board freezes showing yesterday -- and a frozen board is worse
// than a slow one, because it looks fine. So the poll never goes away: it drops
// to a slow interval while the socket is healthy and returns to a brisk one the
// moment it is not.

import { getToken } from '@/auth/authService'
import { apiRequest } from '@/auth/msalConfig'
import { getDisplayKey } from '@/auth/displayKey'

// How often to poll while the socket is working. Slow, but not never: if the
// socket silently stops delivering without dropping -- which a middlebox can
// cause -- this is what eventually notices.
const IDLE_POLL_MS = 60 * 1000

// How often to poll while the socket is down. The old behaviour, so a failure
// here is a return to what worked rather than a degradation anybody sees.
const FALLBACK_POLL_MS = 3 * 1000

// Reconnect backoff. Starts quick because most drops are momentary, and gives
// up climbing at half a minute so a screen recovers promptly after an outage
// rather than sitting out the next twenty minutes.
const RECONNECT_MIN_MS = 1000
const RECONNECT_MAX_MS = 30 * 1000

function socketUrl() {
  // Same host, so it follows whatever the page was served from and needs no
  // configuration per environment. wss when the page is https, or the browser
  // refuses the connection as mixed content.
  const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${scheme}//${window.location.host}/ws/presence`
}

/**
 * Subscribe to presence changes.
 *
 * Calls onChange() whenever something happened, from the socket or from the
 * fallback poll. The caller cannot tell which, and should not need to.
 *
 * Returns a function that stops everything.
 */
export function subscribeToPresence(onChange) {
  let socket = null
  let pollTimer = null
  let reconnectTimer = null
  let reconnectDelay = RECONNECT_MIN_MS
  let stopped = false

  function schedulePoll(interval) {
    clearInterval(pollTimer)
    pollTimer = setInterval(() => {
      if (!stopped) onChange()
    }, interval)
  }

  function scheduleReconnect() {
    if (stopped) return

    clearTimeout(reconnectTimer)
    reconnectTimer = setTimeout(connect, reconnectDelay)

    // Doubling, capped. Without the cap a screen that was offline overnight
    // would wait hours before trying again.
    reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_MAX_MS)
  }

  async function connect() {
    if (stopped) return

    // The credential the socket sends first. A paired screen sends its key; a
    // person sends a bearer token. The server accepts either and validates
    // both the same way it does for HTTP.
    let credential = ''

    const displayKey = getDisplayKey()
    if (displayKey) {
      credential = `Key ${displayKey}`
    } else {
      try {
        const result = await getToken(apiRequest.scopes)
        if (result?.accessToken) credential = `Bearer ${result.accessToken}`
      } catch (err) {
        console.warn('presence socket: could not get a token', err)
      }
    }

    // No credential means no point connecting: the server closes an
    // unauthenticated connection, and a loop of rejected handshakes is worse
    // than polling.
    if (!credential || stopped) {
      schedulePoll(FALLBACK_POLL_MS)
      scheduleReconnect()
      return
    }

    try {
      socket = new WebSocket(socketUrl())
    } catch (err) {
      console.warn('presence socket: could not open', err)
      schedulePoll(FALLBACK_POLL_MS)
      scheduleReconnect()
      return
    }

    socket.onopen = () => {
      // The token goes in the FIRST MESSAGE, not a header or a query string.
      // A browser cannot set headers on a handshake, and a query string ends up
      // in nginx's access log in plain text.
      socket.send(credential)

      reconnectDelay = RECONNECT_MIN_MS
      schedulePoll(IDLE_POLL_MS)

      // Once on connect: the board may have missed something while the socket
      // was down, and this is the moment to find out.
      onChange()
    }

    socket.onmessage = () => {
      // The content does not matter. Anything arriving means "ask again".
      if (!stopped) onChange()
    }

    socket.onclose = () => {
      socket = null
      if (stopped) return

      schedulePoll(FALLBACK_POLL_MS)
      scheduleReconnect()
    }

    socket.onerror = () => {
      // onclose always follows, so the recovery lives there rather than in two
      // places that could both fire.
      if (socket) socket.close()
    }
  }

  // Poll immediately at the fallback rate, then connect. The board is never
  // waiting on a handshake to show anything.
  schedulePoll(FALLBACK_POLL_MS)
  connect()

  return function stop() {
    stopped = true
    clearInterval(pollTimer)
    clearTimeout(reconnectTimer)

    if (socket) {
      // Cleared first so the handler does not schedule a reconnect for a
      // socket we are deliberately closing.
      socket.onclose = null
      socket.close()
      socket = null
    }
  }
}
